import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const COLORS = [
  ['color_primary', 'اللون الأساسي (العناوين)'],
  ['color_secondary', 'اللون الثانوي (الفواصل)'],
  ['color_text', 'لون النصّ'],
];

/**
 * هوية موقع الشيخ التي يُصدَّر بها تفريغه إلى Word وPDF وصفحة الموقع. لا قالب
 * افتراضياً بهوية الجمعية: ما نقص يُسمّى ويمنع التصدير، وتغيير أيّ عنصر يُسقط
 * اعتماد المطابقة حتى يُراجَع.
 */
export default function SheikhIdentityForm({ clientId }) {
  const { api, apiUpload } = useAuth();
  const toast = useToast();
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ site_url: '', color_primary: '', color_secondary: '', color_text: '' });
  const [busy, setBusy] = useState(false);

  const apply = (s) => {
    setState(s);
    setForm({
      site_url: s.site_url || '', color_primary: s.color_primary || '',
      color_secondary: s.color_secondary || '', color_text: s.color_text || '',
    });
  };

  useEffect(() => {
    api(`/clients/${clientId}/identity`).then(apply).catch(err => toast.error(err.message));
  }, [api, clientId, toast]);

  const run = async (fn, done) => {
    setBusy(true);
    try { apply(await fn()); if (done) toast.success(done); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const save = () => run(() => api(`/clients/${clientId}/identity`, {
    method: 'PUT',
    body: JSON.stringify(Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null]))),
  }), 'حُفظت الهوية');

  const uploadLogo = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    run(() => apiUpload(`/clients/${clientId}/identity/logo`, fd), 'رُفع الشعار');
  };

  const verify = () => run(() => api(`/clients/${clientId}/identity/verify`, { method: 'POST' }), 'اعتُمدت الهوية');

  if (!state) return null;

  return (
    <div data-testid="sheikh-identity" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>هوية موقع الشيخ (للتصدير والنشر باسمه)</div>
      <div className="form-group">
        <label className="form-label">رابط الموقع</label>
        <input className="form-input" dir="ltr" placeholder="https://" value={form.site_url}
          onChange={e => setForm(f => ({ ...f, site_url: e.target.value }))} />
      </div>
      <div className="form-row">
        {COLORS.map(([key, label]) => (
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="color" value={form[key] || '#000000'} onChange={e => setForm(f => ({ ...f, [key]: e.target.value.toUpperCase() }))} />
              <input className="form-input" dir="ltr" style={{ width: 100 }} placeholder="#RRGGBB" value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
            </div>
          </div>
        ))}
      </div>
      <div className="form-group">
        <label className="form-label">شعار الموقع (SVG أو PNG عالي الدقة)</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {state.logo_url && <img src={state.logo_url} alt="شعار الموقع" style={{ height: 40, maxWidth: 160, background: '#fff', padding: 4, borderRadius: 4 }} />}
          <input type="file" accept=".svg,.png,.jpg,.jpeg" disabled={busy} onChange={e => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-sm btn-primary" onClick={save} disabled={busy}>حفظ الهوية</button>
        <button className="btn btn-sm btn-outline" onClick={verify} disabled={busy || !!state.identity_verified_at}>
          {state.identity_verified_at ? '✓ معتمدة' : 'اعتماد مطابقتها لموقع الشيخ'}
        </button>
      </div>
      {state.missing?.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--c-coral)', marginTop: 8 }}>
          ناقص (يمنع التصدير): {state.missing.join('، ')}
        </div>
      )}
    </div>
  );
}
